import { useCallback, useEffect, useRef, useState } from "react";
import {
  configureSpeechUtterance,
  resolveGuidedTourSpeechVoice,
  splitTextForSpeech,
} from "./speechSynthesisVoice";

const GUIDED_TOUR_MUTED_KEY = "guided_tour_narration_muted";

export function isGuidedTourNarrationMuted() {
  try {
    return localStorage.getItem(GUIDED_TOUR_MUTED_KEY) === "true";
  } catch {
    return false;
  }
}

export function setGuidedTourNarrationMuted(muted) {
  try {
    if (muted) {
      localStorage.setItem(GUIDED_TOUR_MUTED_KEY, "true");
    } else {
      localStorage.removeItem(GUIDED_TOUR_MUTED_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Browser speech for guided tour Listen (desktop).
 * Uses best available neural/cloud voice; server TTS can replace speak() later.
 */
export default function useGuidedTourNarration() {
  const utteranceRef = useRef(null);
  const speakSessionRef = useRef(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const stop = useCallback(() => {
    speakSessionRef.current += 1;
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    utteranceRef.current = null;
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    (text) => {
      if (!text?.trim()) return false;
      if (isGuidedTourNarrationMuted()) return false;
      if (typeof window === "undefined" || !window.speechSynthesis) {
        return false;
      }

      stop();

      const sessionId = speakSessionRef.current;
      const synth = window.speechSynthesis;
      const chunks = splitTextForSpeech(text);

      const runSpeech = async () => {
        const voice = await resolveGuidedTourSpeechVoice();
        if (sessionId !== speakSessionRef.current) return;

        setIsSpeaking(true);

        for (let i = 0; i < chunks.length; i += 1) {
          if (sessionId !== speakSessionRef.current) return;

          const chunk = chunks[i];
          const utterance = new SpeechSynthesisUtterance(chunk);
          configureSpeechUtterance(utterance, voice);
          utteranceRef.current = utterance;

          await new Promise((resolve) => {
            utterance.onend = () => resolve();
            utterance.onerror = () => resolve();
            synth.speak(utterance);
          });

          utteranceRef.current = null;
        }

        if (sessionId === speakSessionRef.current) {
          setIsSpeaking(false);
        }
      };

      runSpeech().catch(() => {
        if (sessionId === speakSessionRef.current) {
          setIsSpeaking(false);
        }
      });

      return true;
    },
    [stop]
  );

  useEffect(() => {
    resolveGuidedTourSpeechVoice().catch(() => {});
    return () => stop();
  }, [stop]);

  return { speak, stop, isSpeaking };
}
